// Design tokens. Colours, type sizes and spacing come from here rather than being
// written inline, so a change lands in one place instead of several hundred.
// Added 29-Aug-2026. App.js is being migrated onto this incrementally, so inline
// literals still exist elsewhere in the file and are not yet a defect.
//
// WHY THESE VALUES: an audit of App.js found 41 distinct hex colours, 18 font
// sizes and 25 padding values. Almost none of that variety carried meaning - the
// greys alone ran to 14 shades, and seven font sizes sat between 0.75 and 0.9rem
// doing the same job. What is genuinely semantic is kept and named below; the
// rest is collapsed.

export const C = {
  // Surfaces, darkest to lightest
  bg:         '#0a0a0a',   // page
  surface:    '#111111',   // card and table body
  surfaceAlt: '#161616',   // zebra row, table header cell
  raised:     '#1c1c1c',   // input, chip

  // Lines
  line:       '#262626',   // card border, visible divider
  lineSoft:   '#1a1a1a',   // row separator inside a table

  // Text, strongest to weakest
  text:       '#ededed',
  textMuted:  '#8b8b8b',
  textFaint:  '#5a5a5a',
  textLabel:  '#9a9a9a',   // column header - a label, not an accent
  bandSurface:'rgba(255,255,255,0.018)',  // tinted band behind a content block
  bandLine:   'rgba(255,255,255,0.07)',   // hairline above a band

  // Semantic - these six carry meaning in the data and must NOT be merged
  accent:     '#00d4aa',   // brand, positive, active state
  target:     '#f0c040',   // the target company's own row
  offer:      '#ff9800',   // at-offer-price row
  negative:   '#ff6b6b',   // loss, error, dilution
  info:       '#4fc3f7',   // tooltip affordance
  outlier:    '#b08d3f',   // a multiple shown but excluded from the statistics

  // Tinted row backgrounds, each paired with a semantic colour above
  tintAccent: '#0a2a1e',
  tintTarget: '#1a1a0a',
  tintNeg:    '#2a0a0a',
};

export const F = {
  display: "'Fraunces', Georgia, serif",
  ui:      "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

export const S = {
  display: '2.5rem',
  figure:  '2rem',
  lede:    '1.5rem',    // a prominent figure or heading below a panel's own heading
  h1:      '1.4rem',
  h2:      '1.35rem',
  h3:      '1rem',
  body:    '0.875rem',
  small:   '0.78rem',
  micro:   '0.7rem',
};

// Fraunces at 600 reads heavy on a dark background - the reference sites carry
// display weight through SIZE, not stroke. Display text uses W.normal.
export const W = { normal: 400, medium: 500, semi: 600, bold: 700 };

export const SP = { xxs: '2px', xs: '4px', sml: '6px', sm: '8px', med: '10px', md: '12px', lgr: '14px', lg: '16px', xlr: '20px', xl: '24px', huge: '28px', xxl: '40px', max: '60px' };

export const R = { sm: '8px', md: '12px', pill: '999px' };

// Border widths and the two card radii the app actually uses. Everything else was
// arbitrary variation rather than a decision.
export const BW = { hair: '1px', thick: '2px' };

// Background field, five stacked layers. In CSS the FIRST listed background sits
// on TOP, so the vignette is first: it darkens the outer edges over everything
// below it, which keeps a coloured field reading as atmosphere rather than as a
// hotspot.
//
// ORIENTATION (reversed 29-Aug-2026): the top of the page is near-black so the
// title, inputs and button sit on darkness, and the teal builds LOW, behind the
// example chips and the capability band. The earlier version put the strongest
// bloom behind the title and read as too much colour.
//
// If it needs tuning, move the core alpha (0.17) first; the vignette and the base
// gradient are what keep it from looking cheap, so change those last.
//
// Indigo rather than orange or amber on purpose - both carry meaning in the data
// tables (offer price, target row) and must not be spent on decoration.
export const BG_FIELD = [
  // 1. Vignette, on top: holds the outer edges down, strongest at the very top.
  'radial-gradient(ellipse 130% 80% at 50% 100%, transparent 40%, rgba(0,0,0,0.55) 100%)',
  // 2. Core teal bloom, LOW on the page, behind the chips and the band.
  'radial-gradient(ellipse 1300px 700px at 50% 92%, rgba(0,212,170,0.17), rgba(0,212,170,0.05) 46%, transparent 72%)',
  // 3. Indigo counterweight, lower right, so teal is not the only hue.
  'radial-gradient(ellipse 900px 560px at 88% 78%, rgba(72,112,205,0.14), transparent 66%)',
  // 4. Very faint teal lift, lower left, to spread the colour across the width.
  'radial-gradient(ellipse 900px 500px at 8% 88%, rgba(0,212,170,0.07), transparent 68%)',
  // 5. Base: true black at the top, resolving to a deep teal-navy toward the foot.
  'linear-gradient(180deg, #08080a 0%, #08080a 34%, #0a1417 72%, #0b1f1d 100%)',
].join(', ');
